import { assembleIsraeliIban, splitIsraeliIban } from 'lib/iban';

import { OpenFinanceFormShape } from './schema';

// Only IBAN is supported now (the type selector was removed), but the backend
// still expects the `accountType` discriminator — always 'iban'.
const ACCOUNT_TYPE_IBAN = 'iban';

export interface OpenFinancePayload {
  name: string;
  glBankAccountId: string;
  psuId: string;
  providerId: string;
  accountType: string;
  accountNumber: string;
}

// Form values → API body. The entered part fields are dropped; the assembled
// Israeli IBAN ('IL' + check digits + zero-padded BBAN) is sent as a single
// `accountNumber` alongside the constant `accountType: 'iban'`.
export const toPayload = (values: OpenFinanceFormShape): OpenFinancePayload => ({
  name: values.name,
  glBankAccountId: values.glBankAccountId,
  // PSU ID is validated as 5–9 digits; the backend expects a fixed 9-digit
  // value, so left-pad with zeros (e.g. '12345' → '000012345').
  psuId: values.psuId.padStart(9, '0'),
  providerId: values.providerId,
  accountType: ACCOUNT_TYPE_IBAN,
  accountNumber: assembleIsraeliIban({
    bankCode: values.bankCode,
    branchCode: values.branchCode,
    accountNumber: values.accountNumber,
  }),
});

export const emptyDefaults = (): OpenFinanceFormShape => ({
  name: '',
  psuId: '',
  glBankAccountId: '',
  providerId: '',
  bankCode: '',
  branchCode: '',
  accountNumber: '',
});

// Build edit-mode form defaults from a stored account. The stored IBAN is split
// back into its bank / branch / account parts (after stripping the
// 'IL' + check-digit prefix).
export const fromAccountNumber = (record: any): OpenFinanceFormShape => {
  const parts = splitIsraeliIban(record?.accountNumber ?? '');

  return {
    name: record?.name ?? '',
    psuId: record?.psuId ?? '',
    glBankAccountId: record?.glBankAccountId ?? '',
    providerId: record?.providerId ?? '',
    bankCode: parts.bankCode,
    branchCode: parts.branchCode,
    accountNumber: parts.accountNumber,
  };
};
